import React, {FC, memo} from "react";
import {timeRanges, timeRangeTitles} from "../timeRanges";
import {ArrayDataOptions, BookedBy, TablesEnum, SortOrder, useGetBookingsOverviewOptimizedQuery, GetBookingsOverviewOptimizedQueryVariables} from "../../../../generated/graphql";
import {Moment} from "moment";
import BookingList from "../../../bookings/BookingList/BookingList";
import {TableType} from "../../../bookings/BookingList/BookingListColumns";
import {useLocalStorageOptions} from "../../../../helpers/columnsLocalStorageHandler";

type ShowBookingsProps = {timeRangeString: keyof typeof timeRanges};

const ShowBookings: FC<ShowBookingsProps> = ({timeRangeString}) => {
    const tablesEnum = `BookingsShow${timeRangeString}` as TablesEnum;
    const [timeRange, setTimeRange] = React.useState<[Moment, Moment]>(timeRanges[timeRangeString]);

    const timeRangeTitle = timeRangeTitles[timeRangeString];

    React.useEffect(() => {
        setTimeRange(timeRanges[timeRangeString]);
    }, [timeRangeString, timeRange]);

    const startDateTime = timeRange[0].toISOString();
    const endDateTime = timeRange[1].toISOString();

    const filter: ArrayDataOptions["filter"] = [
        {
            column: "dateOfBooking",
            table: "booking",
            values: [startDateTime, endDateTime],
        },
        {
            column: "bookedBy",
            table: "booking",
            values: [BookedBy.Booker],
        },
    ];

    const [options, setOptions] = useLocalStorageOptions({
        localStorageName: tablesEnum,
        defaultOptions: {
            filter,
            limit: 10,
            offset: 0,
            sortOrder: SortOrder.Descending,
        },
        totalResults: 0,
    });

    React.useEffect(() => {
        setOptions({filter});
    }, [timeRangeString]);

    const defaultColumns: Array<Extract<keyof TableType, string>> = [
        // "createDate",
        "dateOfBooking",
        "bookingNumber",
        "courseType",
        "prefixedCourseNumber",
        "bookerFullName",
        "attendeeFullName",
        // "city",
        "location",
        "grossPrice",
        "firstCourseLesson",
        "actions",
    ];
    const {data, loading, refetch, error} = useGetBookingsOverviewOptimizedQuery({
        // OPTIMIZATION: Nicht skippen auch bei leeren options - lade trotzdem Daten
        // skip: false, // optionsLength === 0,
        variables: {
            options: options as GetBookingsOverviewOptimizedQueryVariables["options"],
        },
        fetchPolicy: "network-only",
    });
    // const tableData = data?.bookings?.items;
    const tableData = {
        dataSource: (data?.bookings?.items as Array<TableType> | undefined) ?? ([] as Array<TableType>),
        existMore: data?.bookings?.existsMore ?? false,
        total: data?.bookings?.total ?? 0,
        loading,
    };

    const combineWithRequiredFilters = (newOptions: ArrayDataOptions) => {
        // Beginne immer mit den vordefinierten/erforderlichen Filtern
        const requiredFilters = [...filter];

        // Füge alle anderen Filter hinzu, die nicht die gleichen Spalten/Tabellen wie die vordefinierten betreffen
        const additionalFilters = (newOptions.filter ?? []).filter((newFilter) => {
            // Prüfe ob dieser Filter bereits in den vordefinierten Filtern existiert (gleiche Spalte und Tabelle)
            const isAlreadyRequired = filter.some((requiredFilter) => requiredFilter.column === newFilter.column && requiredFilter.table === newFilter.table);
            return !isAlreadyRequired;
        });

        return {
            ...newOptions,
            filter: [...requiredFilters, ...additionalFilters],
        };
    };

    return (
        <BookingList
            tableData={tableData}
            defaultColumns={defaultColumns}
            options={options}
            setOptions={(newOptions) => {
                const optionsWithRequiredFilters = combineWithRequiredFilters(newOptions);
                setOptions(optionsWithRequiredFilters);
            }}
            refetch={refetch}
            tablesEnum={tablesEnum}
            minPadding={true}
            hideCreate
            hideHeader
            style={{marginTop: -5}}
            emptyText="Keine Onlinebuchungen in diesem Zeitraum"
            defaultPageSize={20}
        />
    );
};

export default memo(ShowBookings);
